import { Clip } from '@stores/MomentMerge/MomentMergeStore';
import { useEffect, useRef } from 'react';
import { ClipStore } from '../types';

interface UseVideoSetupParams {
  store: ClipStore;
  currentClipIndex: number;
  setCurrentClipIndex: (index: number) => void;
  setActiveVideo: (video: 'video1' | 'video2') => void;
  resetVideoState: (options?: { clearClips?: boolean }) => void;
  setupVideoClips: (activeClipIndex: number, timeInClip?: number) => void;
  lastVirtualTimestampRef: React.RefObject<number>;
  previousClipIdsRef: React.RefObject<string[]>;
}

export const useVideoSetup = ({
  store,
  currentClipIndex,
  setCurrentClipIndex,
  setActiveVideo,
  resetVideoState,
  setupVideoClips,
  lastVirtualTimestampRef,
  previousClipIdsRef,
}: UseVideoSetupParams) => {
  const isInitialSetupRef = useRef(true);

  useEffect(() => {
    if (store.clips.length === 0) {
      setCurrentClipIndex(0);
      setActiveVideo('video1');
      resetVideoState({
        clearClips: true,
      });
      lastVirtualTimestampRef.current = 0;
      previousClipIdsRef.current = [];
      return;
    }

    // Check if this is just a reordering (same clips, different order)
    const currentClipIds = store.clips.map((clip: Clip) => clip.id);
    const previousClipIds = previousClipIdsRef.current;

    const isReordering =
      !isInitialSetupRef.current &&
      previousClipIds.length > 0 &&
      currentClipIds.length === previousClipIds.length &&
      currentClipIds.every((id: string) => previousClipIds.includes(id));

    if (isReordering) {
      // For reordering, we don't need to recreate HLS instances
      // Just update the clip setup without clearing
      const clipInfo = store.clipInTimestamp(store.currentTime);
      let newActiveClipIndex = 0;
      let newTimeInClip = 0;

      if (clipInfo && clipInfo.currentClipIndex < store.clips.length) {
        newActiveClipIndex = clipInfo.currentClipIndex;
        newTimeInClip = clipInfo.timeInClip;
      } else {
        console.warn('Invalid clip info after reordering, resetting to start');
        store.setCurrentTime(0);
        newActiveClipIndex = 0;
        newTimeInClip = 0;
      }

      setupVideoClips(newActiveClipIndex, newTimeInClip);
      // Don't call resetVideoState for reordering to preserve HLS instances

      lastVirtualTimestampRef.current = store.currentTime;
      previousClipIdsRef.current = currentClipIds;
      return;
    }

    // For adding/removing clips or initial setup, do full recreation
    const clipInfo = store.clipInTimestamp(store.currentTime);
    let newActiveClipIndex = 0;
    let newTimeInClip = 0;

    if (clipInfo && clipInfo.currentClipIndex < store.clips.length) {
      newActiveClipIndex = clipInfo.currentClipIndex;
      newTimeInClip = clipInfo.timeInClip;
    } else {
      console.warn('Invalid clip info or out of bounds currentTime, resetting to start');
      store.setCurrentTime(0);
      newActiveClipIndex = 0;
      newTimeInClip = 0;
    }

    setupVideoClips(newActiveClipIndex, newTimeInClip);
    resetVideoState({
      clearClips: false,
    });

    lastVirtualTimestampRef.current = store.currentTime;
    previousClipIdsRef.current = currentClipIds;
    isInitialSetupRef.current = false;
  }, [store.clips, resetVideoState, setupVideoClips, store]);
};
